import React from 'react';
import { cn } from '../../lib/utils';
import { Check } from 'lucide-react';

interface TimelineStep {
  label: string;
  date?: string;
  completed: boolean;
  current?: boolean;
}

interface TimelineProgressProps {
  steps: TimelineStep[];
  variant?: 'default' | 'compact';
}

export function TimelineProgress({ steps, variant = 'default' }: TimelineProgressProps) {
  const isCompact = variant === 'compact';

  return (
    <div className={cn("w-full", isCompact ? "py-2" : "py-4")}>
      <div className="flex items-center justify-between relative">
        {/* Progress Line */}
        <div className="absolute left-0 right-0 top-4 h-0.5 bg-border" style={{ zIndex: 0 }}>
          <div 
            className="h-full bg-primary transition-all duration-500"
            style={{ 
              width: `${(steps.filter(s => s.completed).length / (steps.length - 1)) * 100}%` 
            }}
          />
        </div>

        {/* Steps */}
        {steps.map((step, index) => (
          <div 
            key={index} 
            className="flex flex-col items-center relative" 
            style={{ zIndex: 1, flex: 1 }}
          >
            {/* Step Circle */}
            <div
              className={cn(
                "rounded-full transition-all duration-300 flex items-center justify-center",
                isCompact ? "h-6 w-6" : "h-8 w-8",
                step.completed
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : step.current
                  ? "bg-primary/20 border-2 border-primary text-primary"
                  : "bg-background border-2 border-border text-muted-foreground"
              )}
            >
              {step.completed ? (
                <Check className={cn("stroke-[3]", isCompact ? "h-3 w-3" : "h-4 w-4")} />
              ) : (
                <div className={cn("rounded-full bg-current", isCompact ? "h-2 w-2" : "h-2.5 w-2.5")} />
              )}
            </div>

            {/* Step Label */}
            <div className={cn(
              "text-center mt-2",
              isCompact ? "max-w-[60px]" : "max-w-[80px]"
            )}>
              <div
                className={cn(
                  "font-medium leading-tight",
                  isCompact ? "text-xs" : "text-sm",
                  step.completed || step.current
                    ? "text-foreground"
                    : "text-muted-foreground"
                )}
              >
                {step.label}
              </div>
              {step.date && !isCompact && (
                <div className="text-xs text-muted-foreground mt-0.5">
                  {step.date}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
